import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@/components/ui/table';
import { TrendingUp, TrendingDown, DollarSign } from 'lucide-react';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

const IncomeStatement = ({ incomeStatement, onDrillDown }) => {
  if (!incomeStatement) return null;

  const categoryIdMap = incomeStatement.category_id_map || {};

  const handleAmountClick = (name, amount) => {
    const categoryId = categoryIdMap[name];
    if (categoryId && amount !== 0 && onDrillDown) {
      onDrillDown(categoryId, name, amount);
    }
  };

  const renderAmount = (name, amount, hoverClass) => {
    const categoryId = categoryIdMap[name];
    const isClickable = categoryId && amount !== 0;
    
    if (isClickable) {
      return (
        <button
          onClick={() => handleAmountClick(name, amount)}
          className={`hover:underline cursor-pointer transition-colors ${hoverClass}`}
        >
          {formatCurrency(amount)}
        </button>
      );
    }
    return formatCurrency(amount);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Income */}
      <Card className="border-slate-200 flex flex-col">
        <CardHeader className="bg-emerald-50 border-b border-emerald-100">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-emerald-700" />
            <CardTitle className="text-xl font-semibold text-emerald-800">Income</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="pt-4 flex-1 flex flex-col">
          <div className="flex-1">
            {Object.keys(incomeStatement.income || {}).length === 0 ? (
              <p className="text-slate-500 text-sm py-4 text-center">No income recorded</p>
            ) : (
              <Table>
                <TableBody>
                  {Object.entries(incomeStatement.income || {}).map(([name, amount]) => (
                    <TableRow key={name} className="group">
                      <TableCell className="text-slate-700">{name}</TableCell>
                      <TableCell className="text-right text-emerald-600 tabular-nums">
                        {renderAmount(name, amount, "hover:text-emerald-800")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <div className="mt-auto pt-4 border-t-2 border-emerald-200 flex justify-between items-center px-4">
            <span className="font-semibold text-base text-slate-800">Total Income</span>
            <span className="font-semibold text-lg text-emerald-700 tabular-nums">
              {formatCurrency(incomeStatement.total_income)}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Expenses */}
      <Card className="border-slate-200 flex flex-col">
        <CardHeader className="bg-rose-50 border-b border-rose-100">
          <div className="flex items-center gap-2">
            <TrendingDown className="w-6 h-6 text-rose-700" />
            <CardTitle className="text-xl font-semibold text-rose-800">Expenses</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="pt-4 flex-1 flex flex-col">
          <div className="flex-1">
            {Object.keys(incomeStatement.expenses || {}).length === 0 ? (
              <p className="text-slate-500 text-sm py-4 text-center">No expenses recorded</p>
            ) : (
              <Table>
                <TableBody>
                  {Object.entries(incomeStatement.expenses || {}).sort((a, b) => a[0].localeCompare(b[0])).map(([name, amount]) => (
                    <TableRow key={name} className="group">
                      <TableCell className="text-slate-700">{name}</TableCell>
                      <TableCell className="text-right text-rose-600 tabular-nums">
                        {renderAmount(name, amount, "hover:text-rose-800")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <div className="mt-auto pt-4 border-t-2 border-rose-200 flex justify-between items-center px-4">
            <span className="font-semibold text-base text-slate-800">Total Expenses</span>
            <span className="font-semibold text-lg text-rose-700 tabular-nums">
              {formatCurrency(incomeStatement.total_expenses)}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Net Income */}
      <Card className={`border-slate-200 lg:col-span-2 ${incomeStatement.net_income >= 0 ? 'bg-emerald-50' : 'bg-rose-50'}`}>
        <CardContent className="py-5">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <DollarSign className={`w-5 h-5 ${incomeStatement.net_income >= 0 ? 'text-emerald-700' : 'text-rose-700'}`} />
              <span className={`font-semibold text-lg ${incomeStatement.net_income >= 0 ? 'text-emerald-800' : 'text-rose-800'}`}>
                Net Income
              </span>
            </div>
            <span className={`font-semibold text-xl tabular-nums ${incomeStatement.net_income >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
              {formatCurrency(incomeStatement.net_income)}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export { IncomeStatement };
